import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Sparkles,
  Wrench,
  MousePointer2,
  Loader2,
} from "lucide-react";

export type ToolsMode = "automatic" | "rule";

export interface RuleFlags {
  singleBox: boolean;
  seperateBox: boolean;
  staticValue: boolean;
}

interface ToolsViewConfigurationProps {
  layoutId?: string;
  type?: "fielditem" | "lineitem";
  mode?: ToolsMode;
  onModeChange?: (mode: ToolsMode) => void;
  /** Rule-mode toggles. Visible only when mode === "rule". */
  ruleFlags?: RuleFlags;
  onRuleFlagsChange?: (next: RuleFlags) => void;
  onProcess?: () => void;
  /** When true the Process button shows a spinner and is disabled. */
  processing?: boolean;
  onManualAdjustment?: () => void;
  /** "View Rules" button alternative to "Manual Adjustment" when in Rule mode. */
  onViewRules?: () => void;
  defaultExpanded?: boolean;
  selectedRow?: string;
  selectedColumn?: string;
  onRuleSelectionChange?: (selection: {
    selectedRow: string;
    selectedColumn: string;
  }) => void;
}

/**
 * The Configuration block that sits between the tabs and the results
 * area. Mirrors eroyal's layout: Automatic/Rule toggle, conditional
 * rule checkboxes, layout ID, and Process / Manual-Adjustment (or
 * View-Rules in rule mode) buttons.
 */
export function ToolsViewConfiguration({
  layoutId,
  type = "fielditem",
  mode = "automatic",
  onModeChange,
  ruleFlags,
  onRuleFlagsChange,
  onProcess,
  processing = false,
  onManualAdjustment,
  onViewRules,
  defaultExpanded = true,
  selectedRow = "",
  selectedColumn = "",
  onRuleSelectionChange,
}: ToolsViewConfigurationProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const flags: RuleFlags =
    ruleFlags ?? { singleBox: true, seperateBox: false, staticValue: false };

  const setFlag = (key: keyof RuleFlags, value: boolean) => {
    if (!onRuleFlagsChange) return;
    // Mutually exclusive flags (Radio-like behavior)
    if (value) {
      onRuleFlagsChange({
        singleBox: key === "singleBox",
        seperateBox: key === "seperateBox",
        staticValue: key === "staticValue",
      });
    } else {
      // Don't allow unchecking the only active flag
      if (flags[key] && !Object.values(flags).some((v, i) => v && Object.keys(flags)[i] !== key)) {
        return;
      }
      onRuleFlagsChange({ ...flags, [key]: value });
    }
  };

  const rowOptions = Array.from({ length: 100 }, (_, i) => i + 1);
  const colOptions = Array.from({ length: 100 }, (_, i) => i + 1);

  return (
    <section className="border-b border-gray-200">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50"
      >
        <span className="text-sm font-semibold text-gray-900">Configuration</span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {expanded ? (
        <div className="space-y-3 px-4 pb-4">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onModeChange?.("automatic")}
              className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${
                mode === "automatic"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                Automatic
              </span>
            </button>
            <button
              type="button"
              onClick={() => onModeChange?.("rule")}
              className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${
                mode === "rule"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Wrench className="h-3.5 w-3.5" />
                Rule
              </span>
            </button>
          </div>

          {/* Rule mode options */}
          {mode === "rule" ? (
            type === "lineitem" ? (
              <div className="grid grid-cols-2 gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Columns
                  </label>
                  <div className="relative">
                    <select
                      value={selectedColumn}
                      onChange={(e) =>
                        onRuleSelectionChange?.({
                          selectedRow,
                          selectedColumn: e.target.value,
                        })
                      }
                      className="w-full appearance-none rounded-md border border-gray-200 bg-white py-1.5 pl-2.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select Columns</option>
                      {colOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Lineitem Splitting Index
                  </label>
                  <div className="relative">
                    <select
                      value={selectedRow}
                      disabled={!selectedColumn}
                      onChange={(e) =>
                        onRuleSelectionChange?.({
                          selectedRow: e.target.value,
                          selectedColumn,
                        })
                      }
                      className="w-full appearance-none rounded-md border border-gray-200 bg-white py-1.5 pl-2.5 pr-8 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Index</option>
                      {rowOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
                <RuleCheckbox
                  label="Single Box"
                  checked={flags.singleBox}
                  onChange={(v) => setFlag("singleBox", v)}
                />
                <RuleCheckbox
                  label="Seperate Box"
                  checked={flags.seperateBox}
                  onChange={(v) => setFlag("seperateBox", v)}
                />
                <RuleCheckbox
                  label="Static Value"
                  checked={flags.staticValue}
                  onChange={(v) => setFlag("staticValue", v)}
                />
              </div>
            )
          ) : null}

          {/* Layout ID */}
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-600">
              Layout ID
            </label>
            <div className="relative">
              <input
                type="text"
                value={layoutId ?? ""}
                readOnly
                placeholder="—"
                className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-2.5 pr-8 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none"
              />
              {layoutId ? (
                <CheckCircle2 className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-emerald-500" />
              ) : null}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onProcess}
              disabled={processing}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {processing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {processing ? "Processing…" : "Process"}
            </button>
            <button
              type="button"
              onClick={type === "lineitem" ? onManualAdjustment : onViewRules}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm outline outline-1 outline-gray-200 hover:bg-gray-50"
            >
              {type === "lineitem" ? (
                <>
                  <MousePointer2 className="h-3.5 w-3.5" />
                  Manual Adjustment
                </>
              ) : (
                <>
                  <Wrench className="h-3.5 w-3.5" />
                  View Rules
                </>
              )}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function RuleCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="truncate">{label}</span>
    </label>
  );
}
